import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import moment from 'moment';
import find from 'lodash/find';
import debounce from 'lodash/debounce';
import padStart from 'lodash/padStart';

import services from '../../services/services';
import {
  updateRoom,
  connectRooms,
  disconnectRoom,
  getConnectedRooms,
} from '../../services/apiChat';

import { generateRoomURL, getRoomName, getUserFullName } from '../../utils/utilsChat';
import {
  prefixPrevProps,
  stringifyTimePeriod,
  exposeComponentToGlobalScope,
} from '../../utils/utilsGlobal';

import { rerenderOnResize } from '../../decorators';
import {
  TextareaWithFormatting,
  FormattedMarkdown,
  Dropdown,
  Tooltip,
  Avatar,
  Modal,
} from '../../components';

import styles from './RightSideSettings.scss';
import uiStyles from '../../styles/ui.scss';


const mapStateToProps = state => {
  return {
    currentCompany: state.currentCompany,
    currentRoom: state.currentRoom,
  };
};

@connect(mapStateToProps)
@rerenderOnResize()
export class RightSideSettings extends Component {

  state = {
    notes: '',
    isEditingNotes: false,
    isSavingNotes: false,
    isConnectingRooms: false,
    connectRoomSearchQuery: '',
    connectRoomSearchResults: [],
    connectingToGroupRoom: null,
    connectingToGroupRoomClient: null,
    connectingToGroupRoomOptions: null,
  };

  notesTextarea = React.createRef();
  connectRoomSearchInput = React.createRef();
  debouncedOnConnectRoomSearch = debounce(this.onConnectRoomSearch.bind(this), 400);

  DEFAULT_CLIENT_CONNECTING_TO_GROUP_ROOM = {
    title: 'Не знаю',
    value: null,
    dividerBelow: true,
  };

  componentDidMount(){
    const { currentRoom } = this.props;
    this.setState({
      notes: currentRoom.notes || '',
      connectingToGroupRoomClient: this.DEFAULT_CLIENT_CONNECTING_TO_GROUP_ROOM,
    });
    document.addEventListener('keydown', this.onDocumentKeyDown);
    exposeComponentToGlobalScope(this);
  }

  componentDidUpdate(prevProps){
    const { currentRoom } = this.props;
    const { prevCurrentRoom } = prefixPrevProps(prevProps);

    if (currentRoom.id !== prevCurrentRoom.id) {
      this.setState({
        connectRoomSearchQuery: '',
        connectRoomSearchResults: [],
        isConnectingRooms: false,
        connectingToGroupRoom: null,
        connectingToGroupRoomOptions: null,
        connectingToGroupRoomClient: this.DEFAULT_CLIENT_CONNECTING_TO_GROUP_ROOM,
      });
    }
    if (currentRoom.notes !== prevCurrentRoom.notes) {
      this.setState({ notes: currentRoom.notes || '' });
    }
  }

  componentWillUnmount(){
    document.removeEventListener('keydown', this.onDocumentKeyDown);
  }

  onDocumentKeyDown = (e) => {
    const { isEditingNotes } = this.state;
    if (e.key === 'Escape' && isEditingNotes) {
      this.setState({ isEditingNotes: false });
    }
  };

  stringifyWorkHours = (timeString) => {
    if (!timeString) {
      return null;
    }
    const [ hourStr, minuteStr ] = timeString.split(':');
    const localTime = this.convertUTCTimeToLocal({ hour: +hourStr, minute: +minuteStr });
    return [
      localTime.hour ? localTime.hour : '00',
      padStart(localTime.minute, 2, '0')
    ].join(':');
  };

  convertUTCTimeToLocal = ({ hour, minute, second }) => {
    const now = new Date();
    now.setUTCHours(hour || 0, minute || 0, second || 0);
    return {
      hour: now.getHours(),
      minute: now.getMinutes(),
      second: now.getSeconds(),
    };
  };

  processForeignData = (foreignData) => {
    if (!foreignData) {
      return [];
    }
    return foreignData.map(item => {
      if (item.field.type === 'DICTIONARY') {
        const dictValue = find(item.field.dictionary, { foreignId: item.value }) || {};
        return {
          id: item.id,
          title: item.field.title,
          value: dictValue.title,
        }
      }
      else {
        return {
          id: item.id,
          title: item.field.title,
          value: item.value,
        };
      }
    }).filter(item => item.title && item.value);
  };

  onRoomNotesKeyUp = (e) => {
    if (e.key === 'Escape') {
      this.setState({ isEditingNotes: false });
    }
  };

  onRoomNotesChange = (e) => {
    const notes = e.target.value;
    this.setState({ notes });
  };

  onRoomNotesEditClick = async () => {
    this.setState({ isEditingNotes: true }, () => {
      this.notesTextarea.current.element.focus();
    });
  };

  onRoomNotesSaveClick = () => {
    const { notes } = this.state;
    const { currentRoom } = this.props;
    const trimmedNotes = notes.trim();

    this.setState({
      isEditingNotes: false,
      isSavingNotes: true,
    });
    services.roomListSubscription.updateRoomById(currentRoom.id, { notes: trimmedNotes });
    updateRoom(currentRoom.id, { notes: trimmedNotes }).finally(() => {
      this.setState({ isSavingNotes: false });
    });
  };

  getNotesTextareaPlaceholder = (title) => {
    const preposition = /[аоиуэiaoe]/i.test(title[0]) ? 'об' : 'о';
    return `Заметки ${preposition} ${title}...`;
  };

  generateConnectedRoomTitle = (connectedRoom) => {
    let title = getRoomName(connectedRoom.toRoom);
    if (connectedRoom.toClient?.id) {
      title  = `${title},\n ${getUserFullName(connectedRoom.toClient, '\u00A0')}`;
    }
    return title;
  };

  onConnectRoomSearch(searchTerm){
    const { currentRoom } = this.props;
    const currentConnectedRoomIds = currentRoom.connectedRooms.map(connectedRoom => connectedRoom.toRoom.id);

    return services.roomListSubscription.fetchRooms({ searchTerm }).then(rooms => {
      const connectRoomSearchResults = rooms
        .filter(room => currentRoom.id !== room.id && !currentConnectedRoomIds.includes(room.id))
        .map(room => {
          return {
            title: getRoomName(room),
            value: room.id,
            data: room,
          };
        });
      this.setState({ connectRoomSearchResults });
    });
  };

  onConnectRoomRemoveClick = (connectedRoomId) => {
    if ( confirm('Вы действительно хотить отвязать этот чат?') ) {
      const { currentCompany } = this.props;
      disconnectRoom(currentCompany.id, connectedRoomId).then(() => {
        this.updateConnectedRoomsList();
      });
    }
  };

  onConnectRoomSelect = (item) => {
    this.setState({
      connectRoomSearchQuery: '',
      connectRoomSearchResults: [],
    });
    const toRoom = item.data;
    if (toRoom.omnichannelChannelType) {
      this.connectCurrentRoomTo(toRoom);
    }
    else {
      this.onConnectingToGroupRoomModalOpen(toRoom);
    }
  };

  onConnectingToGroupRoomModalOpen = (connectingToGroupRoom) => {
    let connectingToGroupRoomOptions = [ this.DEFAULT_CLIENT_CONNECTING_TO_GROUP_ROOM ];
    this.setState({ connectingToGroupRoom, connectingToGroupRoomOptions });

    return services.roomListSubscription.fetchSingleRoom(connectingToGroupRoom.id).then(roomFull => {
      const memberOptions = roomFull.members.map(member => {
        return {
          title: getUserFullName(member),
          value: member.id,
          data: member,
        };
      });
      this.setState({
        connectingToGroupRoomOptions: [
          ...connectingToGroupRoomOptions,
          ...memberOptions,
        ],
      });
    });
  };

  connectCurrentRoomTo = (toRoom, toClient) => {
    const { currentCompany, currentRoom } = this.props;
    this.setState({ isConnectingRooms: true });
    return connectRooms(currentCompany.id, currentRoom.id, toRoom.id, toClient?.id)
      .then(() => this.updateConnectedRoomsList())
      .finally(() => {
        this.setState({ isConnectingRooms: false });
      });
  };

  connectRoomsModalSubmitClick = () => {
    const { connectingToGroupRoom, connectingToGroupRoomClient } = this.state;
    this.connectCurrentRoomTo(connectingToGroupRoom, connectingToGroupRoomClient.data).then(() => {
      this.setState({
        connectingToGroupRoom: null,
        connectingToGroupRoomOptions: null,
        connectingToGroupRoomClient: this.DEFAULT_CLIENT_CONNECTING_TO_GROUP_ROOM,
      });
    });
  };

  updateConnectedRoomsList = () => {
    const { currentRoom } = this.props;
    getConnectedRooms(currentRoom.id).then(connectedRooms => {
      services.roomListSubscription.updateRoomById(currentRoom.id, { connectedRooms });
    });
  };

  render(){
    const { className, currentCompany, currentRoom } = this.props;
    const {
      notes,
      isEditingNotes,
      isSavingNotes,
      connectRoomSearchQuery,
      connectRoomSearchResults,
      isConnectingRooms,
      connectingToGroupRoom,
      connectingToGroupRoomOptions,
      connectingToGroupRoomClient,
    } = this.state;

    if (!currentRoom.id || currentRoom.id === 'default') {
      return (
        <div className={cn(styles.container, className)}/>
      );
    }

    const trimmedNotes = notes.trim();
    const timezone = new Date().toTimeString().split(/\s/)[1].replace(/00$/, '');
    const foreignData = this.processForeignData(currentRoom.foreignData);
    const hasRoomOperatorBlock = currentRoom.workHours || currentRoom.assignedOperator.id || currentRoom.avgResponseTime;
    const isReducedSize = window.innerWidth < 1035; // 1035px is $grid-breakpoint-attachment-preview (see variables.scss)

    return (
      <div className={cn({
        [className]: true,
        [styles.container]: true,
        [styles.containerOverflowVisible]: Boolean(connectingToGroupRoom),
      })}>

        {Boolean(connectingToGroupRoom) && (
          <Modal
            title={getRoomName(connectingToGroupRoom)}
            onClose={() => this.setState({ connectingToGroupRoom: null })}
            verticallyCentered={true}
            overrideClassNames={{
              title: styles.connectRoomsModalTitle,
            }}>
            C кем именно в {getRoomName(connectingToGroupRoom)} связать чат {getRoomName(currentRoom)}?

            <Dropdown
              items={connectingToGroupRoomOptions}
              onChange={connectingToGroupRoomClient => this.setState({ connectingToGroupRoomClient })}
              buttonAppearance={true}
              overrideClassNames={{
                container: styles.connectRoomsModalDropdown,
                button: styles.connectRoomsModalDropdownButton,
              }}>
              {connectingToGroupRoomClient.title}
            </Dropdown>

            <button className={cn({
              [uiStyles.buttonPrimary]: true,
              [styles.connectRoomsModalButton]: true,
              [styles.connectRoomsModalButtonIsConnecting]: isConnectingRooms,
            })} onClick={this.connectRoomsModalSubmitClick}>
              Связать чаты
            </button>
          </Modal>
        )}

        <dl className={cn(styles.list, styles.connectedRooms)}>
          <dt className={styles.title}>Связанные чаты</dt>
          <dd className={styles.value}>

            {Boolean(currentRoom.connectedRooms?.length) && (
              <ul className={styles.connectedRoomsList}>
                {currentRoom.connectedRooms.map((connectedRoom, i) => (
                  <li key={i} className={styles.connectedRoomsItem}>
                    <Avatar
                      room={connectedRoom.toRoom}
                      overrideClassNames={{
                        avatar: styles.connectedRoomsAvatar,
                        avatarIcon: styles.connectedRoomsAvatarIcon,
                      }}/>
                    <Tooltip title={this.generateConnectedRoomTitle(connectedRoom)} overflownOnly={true}>
                      <Link className={cn(styles.connectedRoomsTitle, uiStyles.linkBlack)}
                        to={generateRoomURL(currentCompany.id, connectedRoom.toRoom.id)}>
                        {this.generateConnectedRoomTitle(connectedRoom)}
                      </Link>
                    </Tooltip>
                    <Tooltip title="Отвязать чат" align="right">
                      <i className={cn(styles.connectedRoomsRemove, 'icon-close-thick')}
                        onClick={() => this.onConnectRoomRemoveClick(connectedRoom.connectedRoomId)}/>
                    </Tooltip>
                  </li>
                ))}
              </ul>
            )}

            <Dropdown
              overrideClassNames={{
                list: styles.connectedRoomsDropdownList,
                item: styles.connectedRoomsDropdownItem,
                itemFocused: styles.connectedRoomsDropdownItemFocused,
                itemSelected: styles.connectedRoomsDropdownItemSelected,
              }}
              dontKeepSelectedOption={true}
              items={connectRoomSearchResults}
              onChange={this.onConnectRoomSelect}
              renderItem={item => (
                <Fragment>
                  <Avatar
                    room={item.data}
                    overrideClassNames={{
                      avatar: styles.connectedRoomsAvatar,
                      avatarDefaultColor: styles.connectedRoomsAvatarDefaultColor,
                      avatarIcon: styles.connectedRoomsAvatarIcon,
                    }}/>
                  {getRoomName(item.data)}
                </Fragment>
              )}
              renderListTop={() => (
                <input
                  className={styles.connectedRoomsDropdownInput}
                  ref={this.connectRoomSearchInput}
                  type="text"
                  placeholder="Поиск чата"
                  value={connectRoomSearchQuery}
                  onChange={(e) => {
                    this.setState({ connectRoomSearchQuery: e.target.value });
                    this.debouncedOnConnectRoomSearch(e.target.value);
                  }}/>
              )}>
              <button className={cn({
                [uiStyles.buttonSecondarySmall]: true,
                [styles.connectedRoomsButton]: true,
                [styles.connectedRoomsButtonIsConnecting]: isConnectingRooms,
              })}
                onClick={() => requestAnimationFrame(() => { this.connectRoomSearchInput.current?.focus?.() })}>
                Связать с чатом
              </button>
            </Dropdown>
          </dd>
        </dl>

        <dl className={cn(styles.list, styles.listNotes)}>
          <dt className={styles.title}>Комментарий</dt>
          <dd className={styles.value}>

            {isEditingNotes && (
              <Fragment>
                <TextareaWithFormatting
                  className={styles.notesTextarea}
                  placeholder={this.getNotesTextareaPlaceholder(currentRoom.title)}
                  ref={this.notesTextarea}
                  value={notes}
                  minRows={2}
                  maxRows={10}
                  onKeyUp={this.onRoomNotesKeyUp}
                  onChange={this.onRoomNotesChange}>
                </TextareaWithFormatting>
                <div>
                  <button className={uiStyles.buttonSecondarySmall} onClick={this.onRoomNotesSaveClick}>
                    Сохранить
                  </button>
                </div>
              </Fragment>
            )}

            {!isEditingNotes && (
              <Fragment>
                {Boolean(trimmedNotes) && (
                  <FormattedMarkdown className={styles.notesText} markdown={trimmedNotes}/>
                )}
                <div>
                  <button
                    className={cn({
                      [uiStyles.buttonSecondarySmall]: true,
                      [styles.notesButtonAdd]: !trimmedNotes,
                    })}
                    onClick={this.onRoomNotesEditClick}
                    disabled={isSavingNotes}>
                    {isSavingNotes ? 'Сохранение...' : (trimmedNotes ? 'Изменить' : 'Добавить')}
                  </button>
                </div>
              </Fragment>
            )}
          </dd>
        </dl>

        <dl className={styles.list}>
          {currentRoom.workHours && (
            <Fragment>
              <dt className={styles.title}>
                Рабочие часы ({timezone})
              </dt>
              <dd className={styles.value}>
                {this.stringifyWorkHours(currentRoom.workHours.begin)}
                &nbsp;—&nbsp;
                {this.stringifyWorkHours(currentRoom.workHours.end)}
              </dd>
            </Fragment>
          )}

          {currentRoom.assignedOperator.id && (
            <Fragment>
              <dt className={styles.title}>
                {isReducedSize ? 'Ответственный' : 'Ответственный менеджер'}
              </dt>
              <dd className={styles.value}>{getUserFullName(currentRoom.assignedOperator)}</dd>
            </Fragment>
          )}

          {currentRoom.avgResponseTime && (
            <Fragment>
              <dt className={styles.title}>
                {isReducedSize ? 'Ср.' : 'Среднее'} время ответа
              </dt>
              <dd className={styles.value}>
                {stringifyTimePeriod(moment.duration(currentRoom.avgResponseTime).asMilliseconds(), {
                  format: 'numeric',
                })}
              </dd>
            </Fragment>
          )}

          {currentRoom.avgCaseCloseTime && (
            <Fragment>
              <dt className={styles.title}>
                {isReducedSize ? 'Ср.' : 'Среднее'} время закрытия кейса
              </dt>
              <dd className={styles.value}>
                {stringifyTimePeriod(moment.duration(currentRoom.avgCaseCloseTime).asMilliseconds(), {
                  format: 'numeric',
                })}
              </dd>
            </Fragment>
          )}
        </dl>

        {Boolean(foreignData.length) && (
          <dl className={cn({
            [styles.list]: true,
            [styles.listHasRoomOperatorBlock]: hasRoomOperatorBlock,
          })}>
            {foreignData.map(item => (
              <Fragment key={item.id}>
                <dt className={styles.title}>{item.title}</dt>
                <dd className={styles.value}>
                  <FormattedMarkdown markdown={item.value}/>
                </dd>
              </Fragment>
            ))}
          </dl>
        )}
      </div>
    );
  }
}
